import * as React from 'react'
import { connect } from 'react-redux'
import withQueryParams, { InjectedQueryParams } from 'react-router-query-params'

import Moment from 'react-moment'
import { Table, Collapse } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { RootState } from '../../store/rootReducer'
import { Scan } from '../../lib/interfaces/Scan.interface'
import { Employee } from '../../lib/interfaces/Employee.interface'
import { Location } from '../../lib/interfaces/Location.interface'
import { ScansByEmployee } from '../../lib/interfaces/ScansByEmployee.interface'
import { BaseReportFilterParameters } from '../../lib/interfaces/BaseReportFilterParameters.interface'

import * as scansActions from '../../store/scans/actions'
import * as employeesActions from '../../store/employees/actions'

import Breadcrumbs from '../Breadcrumbs.component'
import ScanFilter from './components/ScanFilter.component'

import * as authSelectors from 'src/store/auth/selectors'

import { numberArrayDefaultValue } from '../../utils/query-params'
import { formatName, formatMomentDuration } from '../../lib/helpers/format'

const get = require('lodash/get')
const map = require('lodash/map')
const first = require('lodash/first')
const indexOf = require('lodash/indexOf')
const isEmpty = require('lodash/isEmpty')
const includes = require('lodash/includes')

const moment = require('moment')

interface ScanByEmployeeListProps extends InjectedQueryParams<BaseReportFilterParameters> {
  scans?: Scan[]
  isFetchingScans: boolean
  principalLocation?: Location
  scansByEmployee?: ScansByEmployee[]
  filterParams: BaseReportFilterParameters
  setCurrentEmployee: (employee: Employee) => void
	setFilterParams: (filterParams: BaseReportFilterParameters) => void
	fetchScansListByEmployee: (next?: (err?: any, scans?: ScansByEmployee[]) => void) => void
}

interface ScanByEmployeeListState {
  showEmployeeName: boolean
  expandedEmployees: string[]
}

class ScanByEmployeeListPage extends React.PureComponent<ScanByEmployeeListProps, ScanByEmployeeListState> {
  state: ScanByEmployeeListState = {
    expandedEmployees: [],
    showEmployeeName: true
  }

  componentDidMount() {
    const { queryParams, setFilterParams, fetchScansListByEmployee } = this.props

    setFilterParams(queryParams)
    
    if (queryParams.location !== undefined) {
      fetchScansListByEmployee()
    }
  }

  onFiltersChange = (filters: BaseReportFilterParameters) => {
    this.props.setFilterParams(filters)
		this.props.setQueryParams(filters)
		this.props.fetchScansListByEmployee()

    this.setState({ expandedEmployees: [] })
  }

  handleExpandEmployee(employeeId: string) {
    const { expandedEmployees } = this.state

    const newExpandedEmployees = [ ...expandedEmployees ]

    const elementIndex = indexOf(newExpandedEmployees, employeeId)
    if (elementIndex >= 0) {
      newExpandedEmployees.splice(elementIndex, 1)
    }
    else {
      newExpandedEmployees.push(employeeId)
    }

    this.setState({ expandedEmployees: newExpandedEmployees })
  }

  calculateTotalScans(scansByEmployee?: ScansByEmployee[]): number {
    if (!scansByEmployee || !scansByEmployee.length) {
      return 0
    }
    const totalCounts = scansByEmployee.map(i => i.scansCount).reduce((previousValue, currentvalue) => previousValue + currentvalue)    
    return totalCounts
  }

  populateScanAverages(scansByEmployee?: ScansByEmployee[]): void {
    if (!scansByEmployee) {
      return
    }

    scansByEmployee.forEach(record => {

      // Set to zero if no scans to calculate average
      if (record.scans.length < 3) {
        record.averagePerHour = 0
        return
      }
      
      // Picks the first and the second to last. This is important because if we take just the first and last,
      // employees that don't have many hours would show big average.
      // When we do that, we also need to reduce 1 from the scan count, since we are essentially skipping the last (earliest) scan      
      const firstScan = record.scans[0]
      const lastScan = record.scans[record.scans.length-2]
      const timeDifferenceInHours = (moment(lastScan.timeScanned) - moment(firstScan.timeScanned))/60/60/1000      
      record.averagePerHour = (record.scansCount -1)/timeDifferenceInHours
    })
  }

  render() {
		const { showEmployeeName, expandedEmployees } = this.state
    const { filterParams, isFetchingScans, scansByEmployee } = this.props

    const totalCount = this.calculateTotalScans(scansByEmployee)
    this.populateScanAverages(scansByEmployee)

    return (
      <div className="d-flex flex-column min-height-100">
        <Breadcrumbs breadcrumbs={[
          { name: 'Dashboard', link: '/dashboard' },
          { name: 'Scan Reports', active: true },
        ]} />

        <div className="page applications-page flex-grow-1 min-height-0">
          <section>
            <div className="d-flex align-items-center mb-3">
              <h5 className="title mb-0">Scans By Employee Report</h5>
              {isFetchingScans && <FontAwesomeIcon spin size="lg" className="ml-3" icon={['fas', 'spinner']} />}
            </div>

            <ScanFilter
              filterParams={filterParams}
              onChange={this.onFiltersChange}
              showEmployeeNameChecked={showEmployeeName}
              onChangeShowEmployeeName={checked => this.setState({ showEmployeeName: checked })}
            />

            <h3>Total Scans: {totalCount}</h3>

						<Table hover className="ggtable">
							<thead>
								<tr>
									<th style={{ width: '20%' }}>ID</th>
									{showEmployeeName && <th style={{ width: '40%' }}>Driver</th>}
                  <th style={{ width: '20%' }}>Scan/Hour</th>
									<th style={{ width: '20%' }}>Scans</th>
								</tr>
							</thead>
          
							<tbody>
								{map(scansByEmployee, scan => {
									let employeeName = scan.employeeId
									if (scan.employee) {
										employeeName = (
											<u
												onClick={e => {
													e.stopPropagation()
													this.props.setCurrentEmployee(scan.employee)
												}}
											>
												{formatName(scan.employee)}
											</u>
										)
									}

									const isRowExpanded = includes(expandedEmployees, scan.employeeId)

									return (
										<React.Fragment>
											<tr key={scan.employeeId} onClick={() => this.handleExpandEmployee(scan.employeeId)}>
												<td>{scan.employeeId}</td>
												{showEmployeeName && <td>{employeeName}</td>}
                        <td>
                          {Number(scan.averagePerHour).toFixed(2)}
                        </td>
												<td>
													<div className="d-flex align-items-center justify-content-between">
														{scan.scansCount}
														<FontAwesomeIcon
															icon={['fas', 'chevron-down']}
															className="ggmenu-collapsible-toggleicon"
															flip={isRowExpanded ? 'vertical' : undefined}
														/>
													</div>
													</td>                          
											</tr>
											<tr className="expandable" key={`${scan.employeeId}-expanded`}>
												<td colSpan={3} className="py-0">
													<Collapse isOpen={isRowExpanded}>
														<div style={{ padding: '0 40px' }}>
															<Table className="ggtable mt-3">
																<tbody>
																	{isRowExpanded && map(scan.scans, (singleScan, index) => {
                                    const timeFormat = 'hh:mm A'

                                    const currentScanTime = moment(singleScan.timeScanned)

                                    const previousScan = scan.scans[index-1]
                                    
                                    let timeDiffLabel
                                    if (previousScan) {
                                      const previousScanTime = moment(previousScan.timeScanned)

                                      const minutesDiff = currentScanTime.diff(previousScanTime, 'minutes')

                                      let color
                                      if (minutesDiff >= 15) {
                                        color = '#d9534f'
                                      }
                                      else if (minutesDiff >= 12) {
                                        color = '#f0ad4e'
                                      }

                                      const timeDiff = formatMomentDuration(moment.duration(currentScanTime.diff(previousScanTime)))
                                      if (color) {
                                        timeDiffLabel = (
                                          <span
                                            className="badge"
                                            style={{
                                              color: 'white',
                                              backgroundColor: color
                                            }}
                                          >
                                            {timeDiff}
                                          </span>
                                        )
                                      }
                                      else {
                                        timeDiffLabel = timeDiff
                                      }
                                    }

																		return (
                                      <tr key={singleScan.id}>
                                        <td style={{ width: '30%' }}>
                                          {get(singleScan, 'position.name', '-')}
                                          {' at '}
                                          <Moment format={timeFormat} date={singleScan.timeScanned} />
                                        </td>
                                        <td style={{ width: '30%' }}>{timeDiffLabel}</td>
                                        <td style={{ width: '30%' }}>{singleScan.vin || '-'}</td>
                                        <td style={{ width: '40%' }}>
                                          {singleScan.manualEntry && <FontAwesomeIcon icon={['fas', 'keyboard']} color="red" />}
                                        </td>
                                      </tr>
                                    )
                                  })}
																</tbody>
															</Table>
														</div>
													</Collapse>
												</td>
											</tr>
										</React.Fragment>
									)
								})}
							</tbody>
						</Table>
          </section>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  scans: state.scans.scans,
  filterParams: state.scans.filterParams,
  isFetchingScans: state.scans.isFetchingScans,
  scansByEmployee: state.scans.scansByEmployee,
  principalLocation: authSelectors.getPrincipalLocation(state)
})

const mapDispatchToProps = (dispatch) => ({
  setCurrentEmployee: (employee: Employee) => dispatch(employeesActions.setCurrentEmployee(employee)),
  setFilterParams: (filterParams: BaseReportFilterParameters) =>
		dispatch(scansActions.setScansFilterParams(filterParams)),
	fetchScansListByEmployee: (next?: (err?: any, scans?: ScansByEmployee[]) => void) =>
		dispatch(scansActions.fetchScansListByEmployee(next))
})

export default connect(mapStateToProps, mapDispatchToProps)(
  withQueryParams<BaseReportFilterParameters, ScanByEmployeeListProps>({
    stripUnknownKeys: true,
    keys: {
      location: {
        validate: () => false,
        default: (value, props) => {
          if (value) { return first(numberArrayDefaultValue(value)) }

          if (props.filterParams.location !== undefined) {
            return props.filterParams.location
          }
          
          if (props.principalLocation !== undefined && props.principalLocation.code !== 'MCO') {
            return props.principalLocation.id
          }

          return undefined
        }
      },
      position: {
        validate: () => false,
        default: (value, props) => {
          if (value) { return numberArrayDefaultValue(value) }
          return props.filterParams.position
        }
      },
      fromDate: {
        default: (value, props) => props.filterParams.fromDate,
        validate: value => {
          if (!value) { return false }
          return moment(value).isValid()
        }
      }
    }
  }
)(ScanByEmployeeListPage))


// WEBPACK FOOTER //
// ./src/routes/scans/ScanByEmployeeList.page.tsx